import type { MfaStatus } from "./data";

/**
 * When the 6 digit code is asked (owner's decision, 2026-11-02). The sign in session keeps the
 * second step (Supabase Auth: aal2) until the user signs out. On top of that this browser remembers:
 *   - without the tick: the code is asked again whenever the browser (the tab) is opened again
 *     (a mark in sessionStorage);
 *   - with the tick "Ezen az eszközön 30 napig ne kérdezze": not asked again on this device for
 *     30 days (a time stamp in localStorage), unless the session lost the second step (sign out,
 *     password change elsewhere, operator reset): then always.
 * Storage may be blocked (private window): then the code is simply asked more often.
 */
export const MFA_TRUST_DAYS = 30;

const trustKey = (userId: string) => `garagevo.mfaTrust.${userId}`;
const sessionKey = (userId: string) => `garagevo.mfaSession.${userId}`;

function read(storage: () => Storage | undefined, key: string): string | null {
  try {
    return storage()?.getItem(key) ?? null;
  } catch {
    return null;
  }
}
function write(storage: () => Storage | undefined, key: string, value: string | null) {
  try {
    if (value === null) storage()?.removeItem(key);
    else storage()?.setItem(key, value);
  } catch {
    // blocked: the code is asked again next time
  }
}
const local = () => globalThis.localStorage;
const session = () => globalThis.sessionStorage;

/** Whether this device is trusted (the tick, less than 30 days ago). */
export function deviceTrusted(userId: string, now = Date.now()): boolean {
  const at = Date.parse(read(local, trustKey(userId)) ?? "");
  return Number.isFinite(at) && at <= now && now - at < MFA_TRUST_DAYS * 864e5;
}

/** Whether the code screen must come now. */
export function codeNeeded(userId: string, status: MfaStatus, now = Date.now()): boolean {
  if (!status.factor) return false;
  if (!status.verified) return true;
  if (read(session, sessionKey(userId)) === "1") return false;
  return !deviceTrusted(userId, now);
}

/** After the right code (or setting it up): remembered for this tab, and with the tick for 30 days. */
export function rememberCode(userId: string, trust: boolean, now = Date.now()) {
  write(session, sessionKey(userId), "1");
  write(local, trustKey(userId), trust ? new Date(now).toISOString() : null);
}

/** Two-step sign in switched off or reset: nothing is remembered. */
export function forgetDevice(userId: string) {
  write(session, sessionKey(userId), null);
  write(local, trustKey(userId), null);
}
